import * as React from 'react';
import { CircularProgress } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { AnimatedItem } from '../../../components/animations/AnimatedItem';

// Fixed, single accent - explicit request ("some have different colour loaders, I want
// consistency"). Was previously tinted per-widget (each card's own accent), which meant this
// one small piece read as a different colour depending which of the six widgets it sat in
// (blue/teal/pink) rather than as one consistent "refreshing" language across the panel.
// Matches the app's own brand blue (mainTheme.TemplateColor.Primary) already used as the
// shared accent across YourEnquiriesWidget.tsx/YourTasksWidget.tsx/KpiSummaryWidget.tsx.
const REFRESHING_INDICATOR_COLOR = '#5173FF';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Explicit bug report: the old bare 13px spinning ring was too easy to miss,
        // especially for a refresh nobody in front of the screen actually triggered (a
        // subscription update landing in the background, not a click on the header Refresh
        // button). Wrapping it in a soft tinted disc plus an outward-radiating "ping" ring
        // gives the same small, quiet signal real presence in peripheral vision - without
        // growing its footprint enough to disturb a widget's header row, and without adding
        // text that could crowd Quick View's own narrow width.
        pill: {
            position: 'relative',
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: '50%',
            backgroundColor: `${REFRESHING_INDICATOR_COLOR}1A`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        ping: {
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `1.5px solid ${REFRESHING_INDICATOR_COLOR}`,
            animation: '$ping 1400ms cubic-bezier(0, 0, 0.2, 1) infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes ping': {
            '0%': { transform: 'scale(1)', opacity: 0.6 },
            '100%': { transform: 'scale(1.8)', opacity: 0 },
        },
    })
);

// Small "still refreshing" cue shown next to a widget's own heading while its data re-fetches -
// after Quick View's header Refresh button is pressed, or now, a matching subscription update
// on the Enquiry/Task page (see YourEnquiriesWidget.tsx/YourTasksWidget.tsx's own onRefresh).
// Deliberately never the big SkeletonLoader (that's reserved for a widget's genuine first load
// only), and deliberately small/quiet so it doesn't compete with the heading it sits beside or
// hide the data still on screen underneath it. Shared across all six QuickView widgets so
// "refreshing" reads the same way everywhere in the panel rather than some cards flashing a
// skeleton and others going silent. Wrapped in AnimatedItem so it pops in rather than snapping
// into existence, matching this app's own established entrance-motion language.
export const RefreshingIndicator: React.FC = () => {
    const classes = useStyles();
    return (
        <AnimatedItem noFill={true}>
            <span className={classes.pill} role="status" aria-label="Refreshing">
                <span className={classes.ping} />
                <CircularProgress size={12} thickness={5} style={{ color: REFRESHING_INDICATOR_COLOR, flexShrink: 0 }} />
            </span>
        </AnimatedItem>
    );
};

// How long isRefreshing must stay true once set, regardless of how fast the underlying
// operation actually resolves - explicit bug report: on a fast query/local network, a refetch
// can resolve in well under a frame, making this indicator flash for an imperceptible instant
// (effectively "not showing" at all) even though the wiring itself was working correctly.
const MINIMUM_VISIBLE_MS = 500;

/**
 * Drives a widget's own isRefreshing state through one refresh cycle: sets it true
 * immediately, runs `startOperation`, then holds it true for at least MINIMUM_VISIBLE_MS
 * before clearing it - so the indicator above is always genuinely visible, never a flash too
 * quick to perceive. `startOperation` must return a Promise that settles once the widget's own
 * data has actually been updated (an Apollo refetch() promise, or a callback-based fetch
 * wrapped in `new Promise(resolve => fetchX(onSuccess, resolve))` at the call site).
 */
export function runWithMinimumVisibleRefresh(setIsRefreshing: (isRefreshing: boolean) => void, startOperation: () => Promise<unknown>): void {
    const startedAtMs = Date.now();
    setIsRefreshing(true);
    startOperation()
        .catch(() => { /* the operation's own caller already surfaces fetch errors (showNotification/query.error) */ })
        .finally(() => {
            const elapsedMs = Date.now() - startedAtMs;
            const remainingMs = Math.max(0, MINIMUM_VISIBLE_MS - elapsedMs);
            if (remainingMs === 0) {
                setIsRefreshing(false);
            } else {
                setTimeout(() => setIsRefreshing(false), remainingMs);
            }
        });
}
